import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { todayLocal } from '../utils/format';
import type {
  AporteExtra,
  AporteExtraConConcepto,
  AporteExtraCreatePayload,
  AporteExtraUpdatePayload,
  ConceptoAporte,
} from '../types/afiliado';

const QK = {
  byAfiliado: (afiliadoId: string) => ['aportes_extra', afiliadoId] as const,
  conceptos: ['conceptos_aporte'] as const,
};

// ----- LIST por afiliado (con join al concepto) -----

async function fetchAportesExtra(afiliadoId: string): Promise<AporteExtraConConcepto[]> {
  const { data, error } = await supabase
    .from('afiliado_aportes_extra')
    .select('*, concepto:conceptos_aporte(codigo, descripcion, porcentaje)')
    .eq('afiliado_id', afiliadoId)
    .order('fecha_desde', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as AporteExtraConConcepto[];
}

export function useAportesExtra(afiliadoId: string | undefined) {
  return useQuery({
    queryKey: afiliadoId ? QK.byAfiliado(afiliadoId) : ['aportes_extra', 'none'],
    queryFn: () => fetchAportesExtra(afiliadoId!),
    enabled: !!afiliadoId,
  });
}

// ----- CREATE -----

export function useCreateAporteExtra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AporteExtraCreatePayload) => {
      const { data, error } = await supabase
        .from('afiliado_aportes_extra')
        .insert(payload)
        .select('*')
        .single();
      if (error) throw error;
      return data as AporteExtra;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QK.byAfiliado(data.afiliado_id) });
    },
  });
}

// ----- UPDATE -----

export function useUpdateAporteExtra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: AporteExtraUpdatePayload;
    }) => {
      const { data, error } = await supabase
        .from('afiliado_aportes_extra')
        .update(payload)
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as AporteExtra;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QK.byAfiliado(data.afiliado_id) });
    },
  });
}

// ----- DAR DE BAJA (soft delete via fecha_hasta) -----

export function useDarDeBajaAporte() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, afiliado_id }: { id: string; afiliado_id: string }) => {
      const today = todayLocal();
      const { data, error } = await supabase
        .from('afiliado_aportes_extra')
        .update({ fecha_hasta: today })
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return { data: data as AporteExtra, afiliado_id };
    },
    onSuccess: ({ afiliado_id }) => {
      qc.invalidateQueries({ queryKey: QK.byAfiliado(afiliado_id) });
    },
  });
}

// ----- Catalogo: conceptos de aporte (opcionales para asignar) -----

export function useConceptosAporte(soloOpcionales = true) {
  return useQuery({
    queryKey: ['conceptos_aporte', { soloOpcionales }],
    queryFn: async () => {
      let query = supabase.from('conceptos_aporte').select('*').eq('activo', true);
      if (soloOpcionales) query = query.eq('opcional', true);
      const { data, error } = await query.order('descripcion');
      if (error) throw error;
      return (data ?? []) as ConceptoAporte[];
    },
    staleTime: 5 * 60_000,
  });
}
